'use client'

import * as React from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from '../../components'
import { useIsDesktop } from '../../hooks'
import { cn } from '../../lib/utils'

interface ModalProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

function Modal({ open, onOpenChange, children }: ModalProps) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? Dialog : Drawer

  return (
    <Component open={open} onOpenChange={onOpenChange} data-slot='modal'>
      {children}
    </Component>
  )
}

function ModalPortal({ ...props }: React.ComponentProps<typeof DialogPortal>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogPortal : DrawerPortal

  return <Component {...props} data-slot='modal-portal' />
}

function ModalOverlay({
  ...props
}: React.ComponentProps<typeof DialogOverlay>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogOverlay : DrawerOverlay

  return <Component {...props} data-slot='modal-overlay' />
}

function ModalTrigger({
  ...props
}: React.ComponentProps<typeof DialogTrigger>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogTrigger : DrawerTrigger

  return <Component {...props} data-slot='modal-trigger' />
}

function ModalContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogContent : DrawerContent

  return (
    <Component
      className={cn('sm:max-w-[525px]', className)}
      {...props}
      slot='modal-content'
    >
      {children}
    </Component>
  )
}

function ModalHeader({ className, ...props }: React.ComponentProps<'div'>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogHeader : DrawerHeader

  return <Component className={className} {...props} data-slot='modal-header' />
}

function ModalFooter({ className, ...props }: React.ComponentProps<'div'>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogFooter : DrawerFooter

  return <Component className={className} {...props} data-slot='modal-footer' />
}

function ModalTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogTitle>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogTitle : DrawerTitle

  return <Component className={className} {...props} data-slot='modal-title' />
}

function ModalDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogDescription>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogDescription : DrawerDescription

  return (
    <Component className={className} {...props} data-slot='modal-description' />
  )
}

function ModalClose({ ...props }: React.ComponentProps<typeof DialogClose>) {
  const isDesktop = useIsDesktop()
  const Component = isDesktop ? DialogClose : DrawerClose

  return <Component {...props} data-slot='modal-close' />
}

const onCloseModal = () => {
  ;(document.querySelector('[data-slot="modal-close"]') as HTMLElement)?.click()
}

export {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPortal,
  ModalTitle,
  ModalTrigger,
  onCloseModal,
}
